import { useEffect, useRef, useState, type DragEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { MediaAssetPublic } from '@xingroute/schema'
import { Button } from '../ui/Button.js'
import { AuthorizedMediaImage } from '../AuthorizedMediaImage.js'

// WaypointAttachmentsPopover — modal-style overlay for managing the
// image attachments bound to a single waypoint.
//
// Mode:
//   - 'read' : grid + click-to-lightbox only. No upload, no delete.
//   - 'edit' : grid + dropzone + per-thumb delete. Image-only (jpeg/png
//              /webp/gif); video is deferred to a future round so the
//              popover stays compact.
//
// The popover is parent-controlled: parent owns `items`, the open
// state, and the upload/delete callbacks. The callbacks return promises
// so the popover can render an `uploading` spinner without owning auth
// context itself — keeps the component easy to unit-test.

export const WAYPOINT_ATTACHMENTS_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif'

export interface WaypointAttachmentsPopoverProps {
  open: boolean
  mode: 'read' | 'edit'
  waypointLabel: string
  items: MediaAssetPublic[]
  mediaToken?: string | null
  onClose: () => void
  onUpload?: (file: File) => Promise<void>
  onDelete?: (mediaId: string) => Promise<void>
}

export function WaypointAttachmentsPopover({
  open,
  mode,
  waypointLabel,
  items,
  mediaToken,
  onClose,
  onUpload,
  onDelete,
}: WaypointAttachmentsPopoverProps) {
  const { t } = useTranslation('common')
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [uploading, setUploading] = useState(false)
  const [dropActive, setDropActive] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lightboxId, setLightboxId] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setUploading(false)
      setDropActive(false)
      setError(null)
      setLightboxId(null)
    }
  }, [open])

  if (!open) return null

  const canEdit = mode === 'edit'

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0 || !canEdit || !onUpload) return
    setError(null)
    setUploading(true)
    try {
      for (const file of Array.from(files)) {
        await onUpload(file)
      }
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setUploading(false)
    }
  }

  function handleDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault()
    setDropActive(false)
    if (!canEdit) return
    void handleFiles(event.dataTransfer.files)
  }

  async function handleDelete(id: string) {
    if (!canEdit || !onDelete) return
    if (!confirm(t('editor.waypointAttachments.deleteConfirm'))) return
    setError(null)
    try {
      await onDelete(id)
    } catch (err) {
      setError((err as Error).message)
    }
  }

  const lightboxItem = lightboxId ? items.find((item) => item.id === lightboxId) : null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--xr-overlay-bg)] p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('editor.waypointAttachments.title')}
      onClick={onClose}
      data-testid="waypoint-attachments-popover"
    >
      <div
        className="flex max-h-[80dvh] w-full max-w-2xl flex-col overflow-hidden rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] shadow-[var(--xr-shadow-md)]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex flex-none items-center justify-between gap-3 border-b border-[var(--xr-border-default)] px-4 py-3">
          <h2 className="min-w-0 truncate text-base font-semibold text-[var(--xr-text-primary)]">
            {t('editor.waypointAttachments.title')} · {waypointLabel}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex-none rounded-[var(--xr-radius-sm)] px-2 py-1 text-sm text-[var(--xr-text-muted)] hover:bg-[var(--xr-bg-surface-elev)]"
            aria-label={t('aria.close')}
            data-testid="waypoint-attachments-close"
          >
            ×
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
          {canEdit && (
            <input
              ref={fileRef}
              type="file"
              accept={WAYPOINT_ATTACHMENTS_ACCEPT}
              multiple
              className="hidden"
              data-testid="waypoint-attachments-input"
              onChange={(event) => {
                void handleFiles(event.target.files)
                event.target.value = ''
              }}
            />
          )}

          {error && (
            <p
              className="rounded-[var(--xr-radius-sm)] bg-[var(--xr-danger-soft)] px-3 py-2 text-xs text-[var(--xr-danger)]"
              data-testid="waypoint-attachments-error"
              role="alert"
            >
              {error}
            </p>
          )}

          {items.length === 0 && !canEdit ? (
            <p
              className="text-xs text-[var(--xr-text-muted)]"
              data-testid="waypoint-attachments-empty"
            >
              {t('editor.waypointAttachments.empty')}
            </p>
          ) : (
            <ol className="grid grid-cols-5 gap-1.5" data-testid="waypoint-attachments-grid">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="group relative aspect-square overflow-hidden rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface-elev)]"
                  data-testid="waypoint-attachments-thumb"
                >
                  <button
                    type="button"
                    onClick={() => setLightboxId(item.id)}
                    aria-label={t('editor.waypointAttachments.viewAria')}
                    className="block h-full w-full"
                  >
                    <AuthorizedMediaImage
                      source={item.thumb_url ?? item.blob_url}
                      token={mediaToken}
                      alt={item.alt_text ?? ''}
                      className="h-full w-full object-cover"
                      draggable={false}
                      loading="lazy"
                    />
                  </button>
                  {canEdit && (
                    <button
                      type="button"
                      aria-label={t('editor.waypointAttachments.deleteAria')}
                      onClick={(event) => {
                        event.stopPropagation()
                        void handleDelete(item.id)
                      }}
                      className="absolute right-1 top-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-[var(--xr-danger)] bg-[var(--xr-bg-surface)]/90 text-xs font-bold text-[var(--xr-danger)] opacity-0 transition group-hover:opacity-100 focus:opacity-100"
                      data-testid="waypoint-attachments-delete"
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
              {canEdit && (
                <li className="aspect-square" data-testid="waypoint-attachments-upload-tile">
                  <label
                    onDragOver={(event) => {
                      event.preventDefault()
                      setDropActive(true)
                    }}
                    onDragLeave={() => setDropActive(false)}
                    onDrop={handleDrop}
                    onClick={() => fileRef.current?.click()}
                    title={t('editor.waypointAttachments.imagesOnly')}
                    className={
                      'flex h-full w-full cursor-pointer flex-col items-center justify-center gap-0.5 rounded-[var(--xr-radius-md)] border-2 border-dashed text-center transition ' +
                      (dropActive
                        ? 'border-[var(--xr-brand-primary)] bg-[var(--xr-brand-primary-soft)]/40 text-[var(--xr-brand-primary)]'
                        : 'border-[var(--xr-border-strong)] text-[var(--xr-text-muted)] hover:bg-[var(--xr-bg-surface-elev)]')
                    }
                    data-testid="waypoint-attachments-dropzone"
                  >
                    <span className="text-lg leading-none" aria-hidden="true">
                      +
                    </span>
                    {uploading && (
                      <span className="px-1 text-[9px] leading-tight">
                        {t('editor.waypointAttachments.uploading')}
                      </span>
                    )}
                  </label>
                </li>
              )}
            </ol>
          )}
        </div>

        <div className="flex flex-none items-center justify-end gap-2 border-t border-[var(--xr-border-default)] px-4 py-3">
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t('editor.waypointAttachments.close')}
          </Button>
        </div>
      </div>

      {lightboxItem && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4"
          onClick={(event) => {
            event.stopPropagation()
            setLightboxId(null)
          }}
          role="dialog"
          aria-modal="true"
          aria-label={t('editor.waypointAttachments.viewAria')}
          data-testid="waypoint-attachments-lightbox"
        >
          <AuthorizedMediaImage
            source={lightboxItem.medium_url ?? lightboxItem.blob_url}
            token={mediaToken}
            alt={lightboxItem.alt_text ?? ''}
            className="max-h-[90dvh] max-w-[90dvw] object-contain"
            draggable={false}
          />
        </div>
      )}
    </div>
  )
}
